import {
  mapFees,
  mapTelegramStatus,
  mapUserActivity,
  mapUserProfile,
  mapVerifyUser2FA,
} from '@/lib/api/auth/mappers';
import type { Fees, TelegramStatus, UserActivity, UserProfile } from '@/lib/api/auth/models';
import type {
  ConfirmPasswordChangeRequestDto,
  ConfirmPasswordChangeResponseDto,
  DisableTwoFactorRequestDto,
  FeesDto,
  PasskeyCredentialDto,
  PasskeyDeleteRequestDto,
  PasskeyRegisterChallengeDto,
  PasskeyRegisterChallengeRequestDto,
  PasskeyRegisterVerifyRequestDto,
  PasskeyRenameRequestDto,
  PasskeySignInChallengeDto,
  PasskeySignInVerifyRequestDto,
  PasskeySignInVerifyResponseDto,
  RequestPasswordChangeRequestDto,
  RequestPasswordChangeResponseDto,
  Setup2FAResponseDto,
  TelegramLinkInitRequestDto,
  TelegramLinkInitResponseDto,
  TelegramStatusResponseDto,
  TwoFAEnabledResponseDto,
  UnblockAccountRequestDto,
  UnblockAccountResponseDto,
  UpdateLocaleResponseDto,
  UpdateNicknameResponseDto,
  UpdateRegCountryResponseDto,
  UserActivityDto,
  UserProfileDto,
  Verify2FARequestDto,
  Verify2FAResponseDto,
  VerifyUser2FARequestDto,
  VerifyUser2FAResponseDto,
} from '@/lib/api/auth/types';
import { createHttpClient } from '@/lib/api/httpClient';
import { type MappedAuthData, storeObfuscatedAuth } from '@/lib/auth/responseMapper';

// Factory that returns an HTTP client for the auth service.
// Cookies carry auth automatically — no token header needed.
export function createAuthHttp() {
  return createHttpClient({ baseUrl: process.env.NEXT_PUBLIC_API_AUTH_URL });
}
// Transitional alias — cookies mean the token arg is ignored. Remove after Task 3.
export function createAuthHttpWithToken(_token?: string) {
  return createAuthHttp();
}

// types and mappers moved to separate files to mirror /event structure

// Allows the authenticated user to update their nickname
// Endpoint: PATCH /api/user/nickname
// Body: { n: string }
export async function updateUserNickname(nickname: string): Promise<UpdateNicknameResponseDto> {
  const client = createAuthHttp();
  const res = await client.patch<UpdateNicknameResponseDto>('/api/user/nickname', {
    body: { n: nickname },
  });
  return res ?? {};
}

// Updates user locale
// Endpoint: PATCH /api/locale
// Body: { l: string }
export async function updateUserLocale(locale: string): Promise<UpdateLocaleResponseDto> {
  const client = createAuthHttp();
  const res = await client.patch<UpdateLocaleResponseDto>('/api/locale', {
    body: { l: locale },
  });
  return res ?? {};
}

// Returns all activities for the authenticated user
export async function fetchUserActivities(): Promise<UserActivity[]> {
  const client = createAuthHttp();
  const dto = await client.get<UserActivityDto[]>('/api/activity');
  return (dto ?? []).map(mapUserActivity);
}

// Deletes a specific user activity by id
// Endpoint: DELETE /api/activity/{activityId}
export async function deleteUserActivity(activityId: string): Promise<void> {
  const client = createAuthHttp();
  await client.delete<void>(`/api/activity/${activityId}`);
}

// Starts 2FA enrollment and returns otpauth:// URI and secret
// Endpoint: POST /2fa/setup
export async function startTwoFactorSetup(): Promise<Setup2FAResponseDto> {
  const client = createAuthHttp();
  const res = await client.post<Setup2FAResponseDto>('/2fa/setup');
  return res ?? {};
}

// Verifies 2FA code and enables 2FA, returns backup codes
// Endpoint: POST /2fa/verify
// Body: { code: string }
export async function verifyTwoFactor(code: string): Promise<Verify2FAResponseDto> {
  const client = createAuthHttp();
  const res = await client.post<Verify2FAResponseDto>('/2fa/verify', {
    body: { code } as Verify2FARequestDto,
  });
  return res ?? [];
}

// Requests a 6-digit email code for password change
// Endpoint: POST /api/password/change/request
// Body: { oldPassword } — backend verifies current password here
export async function requestPasswordChangeCode(
  payload: RequestPasswordChangeRequestDto,
): Promise<RequestPasswordChangeResponseDto> {
  const client = createAuthHttp();
  const res = await client.post<RequestPasswordChangeResponseDto>('/api/password/change/request', {
    body: payload,
  });
  return res ?? {};
}

// Confirms password change with email code (and optional OTP) and returns fresh JWT
// Endpoint: POST /api/password/change/confirm
// Body: { emailCode, newPassword, otp? }
export async function confirmPasswordChange(
  payload: ConfirmPasswordChangeRequestDto,
): Promise<ConfirmPasswordChangeResponseDto> {
  const client = createAuthHttp();
  const res = await client.post<ConfirmPasswordChangeResponseDto>('/api/password/change/confirm', {
    body: payload,
  });
  return res ?? {};
}

// Disables 2FA for the authenticated user
// Endpoint: DELETE /2fa
export async function disableTwoFactor(payload: DisableTwoFactorRequestDto): Promise<void> {
  const client = createAuthHttp();
  await client.delete<void>('/2fa/disable', {
    body: payload,
  });
}

// Verifies via flag endpoint whether 2FA is enabled for provided email
// Endpoint: GET /2fa/enabled?email=...
export async function fetchTwoFAEnabled(email: string): Promise<TwoFAEnabledResponseDto> {
  const client = createHttpClient();
  const res = await client.get<TwoFAEnabledResponseDto>('/2fa/enabled', {
    query: { email },
  });
  return res ?? {};
}

// Verifies user 2FA (session + otp), returns obfuscated auth payload (token if correct)
// Endpoint: POST /auth/user-verify
// Body: { s: string; o: string }
export async function verifyUser2FA(payload: VerifyUser2FARequestDto): Promise<MappedAuthData> {
  const baseUrl = process.env.NEXT_PUBLIC_API_AUTH_URL;
  const client = createHttpClient({ baseUrl });
  const res = await client.post<VerifyUser2FAResponseDto>('/auth/user-verify', { body: payload });
  if (!res) return {} as MappedAuthData;
  try {
    storeObfuscatedAuth(res);
  } catch {}
  return mapVerifyUser2FA(res);
}

// Unblock account by email and code. Returns obfuscated auth payload
// Endpoint: POST /auth/unblock
// Body: { e: string; o: string; a: { l: string; t: 'MOBILE'|'DESKTOP'|'TABLET'|'BOT'; i: string; c: string } }
export async function unblockAccount(
  payload: UnblockAccountRequestDto,
): Promise<UnblockAccountResponseDto> {
  const baseUrl = process.env.NEXT_PUBLIC_API_AUTH_URL;
  const client = createHttpClient({ baseUrl });
  const res = await client.post<UnblockAccountResponseDto>('/auth/unblock', { body: payload });
  return res ?? ({} as UnblockAccountResponseDto);
}

// Logs out current user session
// Endpoint: DELETE /auth/logout
export async function logout(): Promise<void> {
  const http = createAuthHttp();
  await http.delete<void>('/auth/logout');
}

// Get unauthorized fees (public endpoint, no auth required)
// Endpoint: GET /api/user/unauthorized/fees
export async function getUnauthorizedFees(): Promise<Fees> {
  const client = createAuthHttp();
  const dto = await client.get<FeesDto>('/api/user/unauthorized/fees');
  return mapFees(dto ?? { btf: 0, stf: 0, bmf: 0, smf: 0 });
}

// Get user profile
// Endpoint: GET /api/user/me
// cookieHeader: when provided (SSR), forward the browser cookies to the backend;
// when omitted (client-side), the browser sends cookies automatically via credentials:'include'.
export async function getUserProfile(cookieHeader?: string): Promise<UserProfile> {
  const client = createAuthHttp();
  const dto = await client.get<UserProfileDto>('/api/user/me', {
    headers: cookieHeader ? { Cookie: cookieHeader } : undefined,
  });
  return mapUserProfile(dto ?? {});
}

// Boot session-probe: identical to getUserProfile() but suppresses the onLogout
// side-effect on 401. Use ONLY for the initial AuthProvider mount check so that
// anonymous visitors and the /auth/* pages do not get force-redirected to login.
// Refresh is still attempted (expired-access/valid-refresh sessions are recovered);
// failure simply throws so the caller's .catch() can set user = null.
export async function probeSession(): Promise<UserProfile> {
  const client = createAuthHttp();
  const dto = await client.get<UserProfileDto>('/api/user/me', { suppressLogout: true });
  return mapUserProfile(dto ?? {});
}

// Updates user registration country
// Endpoint: PATCH /api/reg-country
// Body: { rc: string }
export async function updateRegCountry(countryCode: string): Promise<UpdateRegCountryResponseDto> {
  const client = createAuthHttp();
  const res = await client.patch<UpdateRegCountryResponseDto>('/api/reg-country', {
    body: { rc: countryCode },
  });
  return res ?? {};
}

// Initiates Telegram notification linking (telegram-sso-api-contract.md §6).
// Endpoint: POST /api/user/telegram/link-init
// The backend decides the variant from the user's existing Telegram identity
// (the client sends no `withSso` flag): `{ u }` → bot deep-link to open (also
// creates the identity on Start); `{ enabled: true }` → enabled synchronously.
export async function initTelegramLink(
  body?: TelegramLinkInitRequestDto,
): Promise<TelegramLinkInitResponseDto> {
  const client = createAuthHttp();
  const res = await client.post<TelegramLinkInitResponseDto>('/api/user/telegram/link-init', {
    body: body ?? {},
  });
  return res ?? {};
}

// Checks whether the Telegram account is linked
// Endpoint: GET /api/user/telegram/status
export async function getTelegramStatus(): Promise<TelegramStatus> {
  const client = createAuthHttp();
  const dto = await client.get<TelegramStatusResponseDto>('/api/user/telegram/status');
  return mapTelegramStatus(dto ?? { l: false });
}

// Disables Telegram notifications (notification-only — does NOT unlink the
// Telegram SSO identity, which lives in Link Accounts via /auth/sso/identities).
// Endpoint: DELETE /api/user/telegram
export async function disableTelegramNotifications(): Promise<void> {
  const client = createAuthHttp();
  await client.delete<void>('/api/user/telegram');
}

// ---- Passkey (WebAuthn) — see passkey-api-contract.md ----

// Sign-in: get a fresh, single-use challenge. Anonymous, stateless, no side
// effects — safe to call speculatively to feature-detect PASSKEY_ENABLED.
// Endpoint: POST /auth/passkey/challenge
export async function fetchPasskeySignInChallenge(): Promise<PasskeySignInChallengeDto> {
  const client = createAuthHttp();
  const res = await client.post<PasskeySignInChallengeDto>('/auth/passkey/challenge');
  return res as PasskeySignInChallengeDto;
}

// Sign-in: redeem the signed assertion. Success mirrors /auth/login (HttpOnly
// cookies set server-side); every failure is a generic 401 PASSKEY_AUTH_FAILED.
// Endpoint: POST /auth/passkey/verify
export async function verifyPasskeySignIn(
  payload: PasskeySignInVerifyRequestDto,
): Promise<PasskeySignInVerifyResponseDto> {
  const client = createAuthHttp();
  const res = await client.post<PasskeySignInVerifyResponseDto>('/auth/passkey/verify', {
    body: payload,
  });
  return res ?? {};
}

// Enrollment: start a create-credential ceremony (Settings, JWT). `totp`
// required when the account has 2FA enabled.
// Endpoint: POST /auth/passkey/register/challenge
export async function fetchPasskeyRegisterChallenge(
  payload?: PasskeyRegisterChallengeRequestDto,
): Promise<PasskeyRegisterChallengeDto> {
  const client = createAuthHttp();
  const res = await client.post<PasskeyRegisterChallengeDto>('/auth/passkey/register/challenge', {
    body: payload ?? {},
  });
  return res as PasskeyRegisterChallengeDto;
}

// Enrollment: redeem the created credential. Omit `label` to get a backend-
// generated one back for the "Name Your Passkey" modal to prefill.
// Endpoint: POST /auth/passkey/register/verify
export async function verifyPasskeyRegister(
  payload: PasskeyRegisterVerifyRequestDto,
): Promise<PasskeyCredentialDto> {
  const client = createAuthHttp();
  const res = await client.post<PasskeyCredentialDto>('/auth/passkey/register/verify', {
    body: payload,
  });
  return res as PasskeyCredentialDto;
}

// Management: list enrolled passkeys (Settings, JWT)
// Endpoint: GET /auth/passkey/credentials
export async function fetchPasskeyCredentials(): Promise<PasskeyCredentialDto[]> {
  const client = createAuthHttp();
  const res = await client.get<PasskeyCredentialDto[]>('/auth/passkey/credentials');
  return res ?? [];
}

// Management: rename
// Endpoint: PATCH /auth/passkey/credentials/{id}
export async function renamePasskeyCredential(
  id: string,
  label: string,
): Promise<PasskeyCredentialDto> {
  const client = createAuthHttp();
  const res = await client.patch<PasskeyCredentialDto>(`/auth/passkey/credentials/${id}`, {
    body: { label } satisfies PasskeyRenameRequestDto,
  });
  return res as PasskeyCredentialDto;
}

// Management: remove. `totp` required when the account has 2FA enabled.
// Existing sessions signed in with this passkey stay valid.
// Endpoint: DELETE /auth/passkey/credentials/{id}
export async function deletePasskeyCredential(
  id: string,
  payload?: PasskeyDeleteRequestDto,
): Promise<void> {
  const client = createAuthHttp();
  await client.delete<void>(`/auth/passkey/credentials/${id}`, {
    body: payload ?? {},
  });
}
